import React, { useCallback, useMemo, useState } from 'react';
import styles from './bidPriceSetting.module.scss';
import i18n from 'i18n';
import _ from 'lodash';
import { getCurrencyLabel, getDecimalPlaceCount } from 'helper/CurrencyHelper';
import { FormikField } from 'components/common/form/field/FormikField';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';

interface BidPriceSettingProps {
  fieldName: string;
  type: string;
  currency: string;
  optimizes: string[];
  showDetail?: boolean;
}

export const BidPriceSetting: React.FC<BidPriceSettingProps> = ({
  type,
  currency,
  fieldName,
  optimizes,
  showDetail: showDetailProp
}) => {
  const hasShowDetailSetting = showDetailProp !== undefined;
  const [showDetail, setShowDetail] = useState(
    hasShowDetailSetting ? showDetailProp : true
  );

  const bidPriceInputStep = Math.pow(10, getDecimalPlaceCount(currency) * -1);

  const triggerShowDetail = useCallback(() => {
    setShowDetail((value) => !value);
  }, []);

  const renderBidPriceSettings = (settingType: string) => {
    return optimizes.map((optimize) => {
      return (
        <div
          key={`${fieldName}.${settingType}.${optimize}`}
          className={styles.optimizeContainer}
        >
          <span className={styles.optimize}>
            {i18n.t(`bidPriceSetting.optimize.${optimize.toLowerCase()}`)}
          </span>
          <FormikField.InputGroup
            fieldContentWidth={'auto'}
            name={`${fieldName}.${settingType}.${optimize}`}
            prefix={getCurrencyLabel(currency)}
            type='number'
            step={bidPriceInputStep}
          />
        </div>
      );
    });
  };

  const title = useMemo(() => {
    const i18nKey = `bidPriceSetting.labels.${_.camelCase(type)}`;
    return i18n.exists(i18nKey) ? i18n.t(i18nKey) : type;
  }, [type]);

  return (
    <div className={styles.bidPriceSetting}>
      <div className={styles.titleArea}>
        <div className={styles.title}>{title}</div>
        {hasShowDetailSetting && (
          <div
            className={styles.triggerShowDetailBtn}
            onClick={triggerShowDetail}
          >
            <div>
              {showDetail
                ? i18n.t('creativeSetupFlow.labels.closeAdvanceSetting')
                : i18n.t('creativeSetupFlow.labels.showAdvanceSetting')}
            </div>
            <FontAwesomeIcon icon={showDetail ? faCaretDown : faCaretUp} />
          </div>
        )}
      </div>
      {showDetail && (
        <fieldset>
          <div className={styles.autoBidCapContainer}>
            <div className={styles.subTitle}>
              {i18n.t('bidPriceSetting.labels.autoBidCapSetting')}
            </div>
            {renderBidPriceSettings('autoBidCap')}
          </div>
          <div className={styles.bidFloorContainer}>
            <div className={styles.subTitle}>
              {i18n.t('bidPriceSetting.labels.bidFloorSetting')}
            </div>
            {renderBidPriceSettings('bidFloor')}
          </div>
        </fieldset>
      )}
    </div>
  );
};
